import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import BasicDialog from "../../components/base/BasicDialog";
import { FilterTableWrapper } from "../../components/table/BetterFilterTable/BetterFilterTable";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import usePaginationWithLocalSearchParamsAndOrdering from "../../hooks/usePaginationWithLocalSearchParamsAndOrdering";
import { TEXT_FIELD_TYPE, DATE_RANGE_FIELD_TYPE, NUMERIC_RANGE_FIELD_TYPE, BOOLEAN_FIELD_TYPE } from "../../helpers/constants";
import {Checkbox} from "@mui/material";
import toast from "react-hot-toast";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { useToasts } from "../../hooks/useToasts";
import { useUpdateInvoiceMutation } from "../../services/bankingService";
import { useTransfersIndividualBills } from "../../services/bankingService";
import { useForm } from "react-hook-form";
import DateService from "../../services/dateService";
import { formatCurrency } from "../../components/common/PriceDisplay";
import SelectFieldService from "../../services/selectFieldService";
import {Typography} from "@mui/material";

export function MatchInvoiceWithTransferDialog(props) {
    const { t } = useTranslation();

    const [transferDataLocal, setTransferDataLocal] = useState([]);
    const [transferCountLocal, setTransferCountLocal] = useState(0);

    const pagination = usePaginationWithLocalSearchParamsAndOrdering(undefined, props.initialParams);

    const transferSearchParams = useMemo(() => {
        const params = new URLSearchParams(pagination.searchParams);
        params.set("page", String(pagination.page));
        params.set("page_size", String(pagination.pageSize));
        params.set("only_connected", true);
        params.set('transfer_is_booked', true)
        return params;
    }, [pagination.searchParams, pagination.page, pagination.pageSize]);

    const {data: acutalTransferData} = useTransfersIndividualBills(new URLSearchParams({transfer_id: props.transferId}), {enabled:props.transferId !== null});
    const { data: transferData} = useTransfersIndividualBills(transferSearchParams);
    
    useEffect(() => {
        if (transferData?.results) {
            setTransferDataLocal(transferData?.results);
            setTransferCountLocal(transferData.count);
        }
    }, [transferData]);

    const [selectedTransferId, setSelectedTransferId] = useState()
    const handleSelectTransferId = (transferId) => {
        setSelectedTransferId(transferId)
    };

    const handleUnselectAgreementId = (transferId) => {
        setSelectedTransferId(null)
    };

    const { onFormErrors, onServerError } = useToasts();
    const { mutate: updateInvoice, isPending: isUpdatingInvoice } = useUpdateInvoiceMutation({
            onSuccess: () => {
                toast.custom(
                    () => <SnackbarSuccess message={t("successfullyMerged")} />,
                    {
                        position: "bottom-right",
                    },
                );
                props.onClose()
            },
            onError: (error) => {
                onServerError(error);
            },
        });

    const { handleSubmit } = useForm({defaultValues: {}});

    const onSubmit = (data) => {
        updateInvoice({data:{invoice_transfer: selectedTransferId}, id:props.invoiceId})
    }; 

    const getTableFieldsConfig = (excludeFields=[]) => {
        let tableConfig = [
            {
                name: "checkboox",
                filterType: null,
                label: t("select"),
                getValueCallback: (row) => (
                    <Checkbox
                        checked={selectedTransferId===row.id}
                        onChange={(e) => {
                            if (e.currentTarget.checked) {
                                handleSelectTransferId(row.id);
                            } else {
                                handleUnselectAgreementId(row.id);
                            }
                        }}
                    />
                ),
            },
            {
                name: "transfer_payer",
                getValueCallback: (rowData) => rowData.transfer_payer,
                label: t("bankings.transfer_payer"),
                filterType: TEXT_FIELD_TYPE,
                sortable: true,
            },
            {
                name: "created",
                getValueCallback: (rowData) => DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(rowData.created),
                label: t("bankings.transfer_created"),
                filterType: DATE_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    style: { minWidth: "100px" },
                }),
                sortable: true,
            },
            {
                name: "transfer_date",
                getValueCallback: (rowData) => rowData.transfer_date,
                label: t("bankings.transfer_date"),
                filterType: DATE_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    style: { minWidth: "100px" },
                }),
                sortable: true,
            },
            {
                name: "transfer_title",
                getValueCallback: (rowData) => rowData.transfer_title,
                label: t("bankings.transfer_title"),
                filterType: TEXT_FIELD_TYPE,
                sortable: true,
            },
            {
                name: "transfer_payer_iban",
                getValueCallback: (rowData) => rowData.transfer_payer_iban,
                label: t("bankings.transfer_payer_iban"),
                filterType: TEXT_FIELD_TYPE,
                sortable: true,
            },
                {
                name: "transfer_reciver_iban",
                getValueCallback: (rowData) => rowData.transfer_reciver_iban,
                label: t("bankings.transfer_reciver_iban"),
                filterType: TEXT_FIELD_TYPE,
                sortable: true,
            },
            {
                name: "transfer_amount",
                getValueCallback: (rowData) => formatCurrency(rowData.transfer_amount),
                label: t("bankings.transfer_amount"),
                filterType: NUMERIC_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: true,
                minWidth: 120,
                width: 120,
            },
            {
                name: "transfer_agreement_nr",
                getValueCallback: (rowData) => rowData.transfer_agreement_nr,
                label: t("bankings.transfer_agreement_nr"),
                filterType: TEXT_FIELD_TYPE,
                // sortable: true
            },
            {
                name: "agreement_notarial_deed",
                getValueCallback: (rowData) => rowData.agreement_notarial_deed,
                label: t("bankings.agreement_notarial_deed"),
                filterType: TEXT_FIELD_TYPE,
                sortable: true,
            },
            {
                name: "agreement_customer",
                getValueCallback: (rowData) => (rowData.agreement_customer),
                label: t("bankings.agreement_customer"),
                filterType: TEXT_FIELD_TYPE,
            },
            {
                name: "agreement_company",
                getValueCallback: (rowData) => (rowData.agreement_company),
                label: t("bankings.agreement_company"),
                filterType: TEXT_FIELD_TYPE,
            },
            {
                name: "agreement_product",
                getValueCallback: (rowData) => rowData.agreement_product,
                label: t("bankings.agreement_product"),
                filterType: TEXT_FIELD_TYPE,
            },
            {
                name: "has_invoice",
                getValueCallback: (rowData) => SelectFieldService.getLabelOptionsYesOrNo(rowData.has_invoice),
                label: t("bankings.has_invoice"),
                filterType: BOOLEAN_FIELD_TYPE,
            },
        ]

        return tableConfig.filter(config=> !excludeFields.includes(config.name))
    }

    const AcutalTransferTable = ()=>{
           return( 
            <>
                <Typography color={"primary"} variant="h6" textAlign={"left"} marginTop={3}>{t("bankings.actualTransfer")}</Typography>
                <LoaderWrapper showLoader={!acutalTransferData}>
                    <FilterTableWrapper
                        data={acutalTransferData?.results}
                        countRecords={1}
                        filtersOpts={{hideFiltersIconCol: true}}
                        fieldsConfig={getTableFieldsConfig(["checkboox"])}
                        disableRowHighlight={true}
                    />
                </LoaderWrapper>
            </>
        )
    }

    const MergeTransferWithInvoiceTable = () => {
        return( 
            <>
                <Typography color={"primary"} variant="h6" textAlign={"left"} marginTop={3}>{t("bankings.selectTransferToMerge")}</Typography>
                <LoaderWrapper showLoader={!transferDataLocal}>
                    <FilterTableWrapper
                        data={transferDataLocal}
                        initialParams={props.initialParams}
                        countRecords={transferCountLocal}
                        localPagination={pagination}
                        filtersOpts={{
                            showCleanFilterIcon: true,
                        }}
                        tableStyle={{
                            maxHeight: "80vh",
                            overflow: "auto",
                        }}
                        onSearchFiltersChange={pagination.handleChangeSearchParams}
                        fieldsConfig={getTableFieldsConfig()}
                        disableRowHighlight={true}
                    />
                </LoaderWrapper>
            </>
        )
    }

    return (
        <BasicDialog
            open={true}
            onClose={() => props.onClose()}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("bankings.mergeInvoiceWithTransfer")}
            maxWidth={"xl"}
            showDialogActions
            alwaysFullScreen={false}
            contentSx={{ pt: 0 }}
        >
            {props.transferId && AcutalTransferTable()}
            {MergeTransferWithInvoiceTable()}
            <LoadingButton
                loading={isUpdatingInvoice}
                disabled={!selectedTransferId}
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={handleSubmit(onSubmit, onFormErrors)}
                fullWidth
            >
                {t("merge")}
            </LoadingButton>
        </BasicDialog>
    );
}
